import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter } from 'rxjs/operators';
import { MFA_COMPLETED } from '../../../constants';
import {
  ContinueAuthentication,
  InitiateAccountRecovery,
  InitiateAuthentication,
  InitiateSsoSession,
  OperationTypes,
  SelectDevice,
  SubmitEmailOtp,
  SubmitNewPassword,
  SubmitSkymilesNumber
} from '../../../models';
import { LoginOAuthParams } from '../../../models/oauth.interfaces';
import { ContinueAuthenticationService } from '../../multi-factor-authentication-adapter-service/continue-authentication/continue-authentication.service';
import { FlyDeltaPollService } from '../../multi-factor-authentication-adapter-service/fly-delta-poll/fly-delta-poll.service';
import { InitiateAuthenticationService } from '../../multi-factor-authentication-adapter-service/initiate-authentication/initiate-authentication.service';
import { InitiateSelfServicePasswordResetService } from '../../multi-factor-authentication-adapter-service/initiate-self-service-password-reset/initiate-self-service-password-reset.service';
import { InitiateSsoSessionService } from '../../multi-factor-authentication-adapter-service/initiate-sso-session/initiate-sso-session.service';
import { PostSkymilesNumberService } from '../../multi-factor-authentication-adapter-service/post-skymiles-number/post-skymiles-number.service';
import { SelectDeviceService } from '../../multi-factor-authentication-adapter-service/select-device/select-device.service';
import { SubmitEmailOtpService } from '../../multi-factor-authentication-adapter-service/submit-email-otp/submit-email-otp.service';
import { SubmitNewPasswordService } from '../../multi-factor-authentication-adapter-service/submit-new-password/submit-new-password.service';
import { MfaUtilService } from '../../multi-factor-authentication-adapter-service/util-service/mfa-util.service';
import { PostWithFingerPrintService } from '../../remember-me-adapter-service/post-with-fingerprint/post-with-fingerprint-service';
import { SelfServicePasswordResetHelperService } from './self-service-password-reset-helper.service';

@Injectable({
  providedIn: 'root'
})
export class SelfServicePasswordResetWrapperService extends SelfServicePasswordResetHelperService {
  constructor(
    initiateSsoSession: InitiateSsoSessionService,
    postWithFingerPrintService: PostWithFingerPrintService,
    initiateSelfServicePasswordReset: InitiateSelfServicePasswordResetService,
    postSkymilesNumber: PostSkymilesNumberService,
    initiateAuthenticationService: InitiateAuthenticationService,
    selectDevice: SelectDeviceService,
    submitEmailOtp: SubmitEmailOtpService,
    continueAuthentication: ContinueAuthenticationService,
    submitNewPassword: SubmitNewPasswordService,
    mfaUtils: MfaUtilService,
    flyDeltaPoll: FlyDeltaPollService
  ) {
    super(
      initiateSsoSession,
      postWithFingerPrintService,
      initiateSelfServicePasswordReset,
      postSkymilesNumber,
      initiateAuthenticationService,
      selectDevice,
      submitEmailOtp,
      continueAuthentication,
      submitNewPassword,
      mfaUtils,
      flyDeltaPoll
    );
  }

  selfServiceResetPasswordFlow(
    loginOAuthParams: LoginOAuthParams,
    operationType?: OperationTypes,
    skymilesNumber?: string,
    deviceRef?: string,
    otp?: string,
    newPassword?: string,
    flowId?: string
  ): Observable<
    | InitiateAccountRecovery
    | SelectDevice
    | SubmitSkymilesNumber
    | SubmitEmailOtp
    | SubmitNewPassword
    | InitiateAuthentication
    | InitiateSsoSession
    | ContinueAuthentication
    | string
  > {
    let fn;

    const options = {
      INITIATE_RESET_PASSWORD: () => {
        return this.initializeSelfServiceReset(loginOAuthParams);
      },
      SUBMIT_SKYMILES_NUMBER: () => {
        const skyMilesNumber = skymilesNumber ?? '';
        return this.submitSkymilesNumber(loginOAuthParams, skyMilesNumber);
      },
      INITIATE_DEVICE_METHOD: () => {
        const id = flowId ?? this.flowId;
        return this.authenticate(loginOAuthParams, id);
      },
      SELECT_DEVICE_METHOD: () => {
        const deviceReferenceId = deviceRef ?? '';
        return this.selectDeviceMethod(loginOAuthParams, deviceReferenceId);
      },
      SUBMIT_OTP: () => {
        const oneTimePassCode = otp ?? '';
        return this.submitOtp(loginOAuthParams, oneTimePassCode);
      },
      SUBMIT_NEW_PASSWORD: () => {
        const password = newPassword ?? '';
        return this.submitNewPasswordRequest(loginOAuthParams, password);
      },
      INITIATE_FLY_DELTA_POLL: () => {
        return this.initiateFlyDeltaPoll(loginOAuthParams);
      },
      default: () => {
        return this.initializeSelfServiceReset(loginOAuthParams);
      }
    };
    operationType ? (fn = options[operationType]) : (fn = options['default']);
    return fn();
  }

  submitOtp(
    loginOAuthParams: LoginOAuthParams,
    otp: string
  ): Observable<ContinueAuthentication | string> {
    return this.submitEmailOtp
      .submitEmailOtp(loginOAuthParams.oauthConfig, this.flowId, otp)
      .pipe(
        filter((response) => response.status === MFA_COMPLETED),
        concatMap((response: SubmitEmailOtp) => {
          return this.continueAuthentication.continueAuthentication(
            loginOAuthParams.oauthConfig,
            response.id ? response.id : ''
          );
        }),
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }

  submitNewPasswordRequest(
    loginOAuthParams: LoginOAuthParams,
    newPassword: string
  ): Observable<SubmitNewPassword | string> {
    return this.submitNewPassword
      .submitNewPassword(loginOAuthParams.oauthConfig, this.flowId, newPassword)
      .pipe(
        catchError((error) => {
          return of(this.mfaUtils.checkResetPasswordError(error));
        })
      );
  }
}
